export const en = {
  app: {
    api: 'Backoffice API',
    area: 'Backoffice',
    brand: 'Affiliate Platform',
    build: 'Build',
    description:
      'Privileged features remain unavailable until the production identity provider and authorization policy are approved.',
    environment: 'Environment',
    foundation: 'Foundation environment',
    language: 'Language',
    metadata: 'Deployment metadata',
    timezone: 'Display timezone',
    title: 'Backoffice access is not configured',
    version: 'Version',
  },
  nav: {
    groups: {
      overview: 'Overview',
      organization: 'Organization',
      affiliates: 'Affiliates',
      commissions: 'Commissions',
      operations: 'Operations',
      governance: 'Governance',
    },
    dashboard: 'Dashboard',
    partners: 'Partners',
    agents: 'Agents',
    adminUsers: 'Backoffice Users',
    members: 'Members & Referrals',
    affiliateWallets: 'Affiliate Wallets',
    withdrawals: 'Withdrawals',
    games: 'Games',
    commissionRules: 'Commission Rules',
    commissionLedger: 'Commission Ledger',
    transactions: 'Transactions',
    transactionFailures: 'Transaction Failures',
    apiOperations: 'API Operations',
    reconciliation: 'Reconciliation',
    partnerCredit: 'Partner Credit',
    reports: 'Reports',
    reportExports: 'Report Exports',
    approvals: 'Approvals',
    audit: 'Audit',
    securityEvents: 'Security Events',
  },
  common: {
    actions: 'Actions',
    all: 'All',
    empty: 'No results',
    loading: 'Loading…',
    no: 'No',
    notAvailable: 'Not available',
    search: 'Search',
    unknown: 'Unknown',
    yes: 'Yes',
  },
  filterPanel: {
    label: '{{page}} filters',
    more: 'More filters',
    fewer: 'Fewer filters',
    activeCount: '{{count}} active',
    secondary: 'Additional filters',
  },
  pageState: {
    accessDenied: 'Access denied',
    back: 'Back',
    error: 'Unable to load this page',
    noRecords: 'No records found',
    requestId: 'Request ID: {{id}}',
    retry: 'Retry',
    unavailable: 'This feature is currently unavailable',
  },
  auth: {
    accessDenied: 'Access denied',
    accountMenu: 'Account menu',
    authenticated: 'Authenticated',
    error: 'Authentication unavailable',
    loading: 'Checking access…',
    logout: 'Log out',
    mockMode: 'DEV · Mock Auth',
    profile: 'Profile',
    unauthenticated: 'Sign in required',
  },
  errors: {
    generic: 'Something went wrong',
    forbidden: 'You do not have permission to perform this action',
    network: 'The service could not be reached',
    notFound: 'The requested resource was not found',
    retry: 'Try again',
    validation: 'Check the highlighted fields',
  },
  status: {
    active: 'Active',
    approved: 'Approved',
    cancelled: 'Cancelled',
    completed: 'Completed',
    disabled: 'Disabled',
    failed: 'Failed',
    inactive: 'Inactive',
    pending: 'Pending',
    rejected: 'Rejected',
    suspended: 'Suspended',
  },
  dashboard: {
    title: 'Dashboard',
    scope: 'Affiliate operations overview',
    subtitle: 'Monitor affiliate performance, financial signals, and operational queues.',
    summary: 'Summary',
    trends: 'Trends',
    topAffiliates: 'Top affiliates',
    topGames: 'Top games',
    operationalQueues: 'Operational queues',
    reportingAsOf: 'Reporting snapshot as of {{date}} · Asia/Bangkok',
    stale: 'This reporting snapshot is stale. Data is current as of {{date}}.',
    empty: 'No Dashboard activity matches these filters.',
    error: 'Unable to load the Dashboard.',
    trendChartLabel: 'Actual play trend chart',
    trendSummary: 'Trend data summary',
    charts: {
      metric: 'Metric',
      period: 'Period',
      playAmount: 'Actual play',
      transactionCount: 'Transactions',
      showData: 'Show data table',
      trendTable: 'Actual play and transactions by period',
      trendLabelPlayAmount: 'Actual play trend chart',
      trendLabelTransactionCount: 'Transaction count trend chart',
      walletLabel: 'Wallet balance split by currency',
      walletTable: 'Wallet balances by currency',
      topGamesLabel: 'Top games by actual play',
      topGamesLabelForCurrency: 'Top games by actual play in {{currency}}',
      topGamesTable: 'Top games by actual play',
    },
    filters: {
      title: 'Dashboard filters',
      toExclusive: 'Exclusive upper bound',
      gameTypeId: 'Game type ID',
      gameId: 'Game ID',
      currency: 'Currency',
      transactionStatus: 'Transaction status',
      more: 'More filters',
      fewer: 'Fewer filters',
      reset: 'Discard changes',
    },
    status: {
      calculated: 'Calculated',
      rejected: 'Rejected',
    },
    kpis: {
      totalActualPlay: 'Total Actual Play',
      netCommission: 'Net commission',
      commissionReversals: 'Commission reversals',
      transactions: 'Transactions',
      calculated: 'Calculated transactions',
      reversed: 'Reversed / rejected',
      failed: 'Failed transactions',
      activeUsers: 'Active users',
      registrations: 'Registrations',
    },
    wallet: {
      title: 'Wallet balances',
      available: 'Available',
      reserved: 'Reserved',
      view: 'View wallets',
    },
    withdrawals: {
      title: 'Withdrawal exceptions',
      unknown: 'Unknown amount',
      investigate: 'Investigate withdrawals',
    },
    apiHealth: {
      title: 'API health',
      unavailable: 'Not provided by the Dashboard API',
      operational: 'Operational',
      degraded: 'Degraded',
      outage: 'Outage',
      latency: 'P95 latency: {{value}} ms',
      successRate: 'Success rate: {{value}}%',
      checkedAt: 'Checked {{date}}',
    },
    rankings: {
      game: 'Game',
      affiliate: 'Affiliate',
      playAmount: 'Actual play',
      commission: 'Commission',
    },
  },
  games: {
    title: 'Games',
    subtitle: 'Review the game catalog, provider mapping, and affiliate availability.',
    tabs: {
      label: 'Game Master sections',
      gameTypes: 'Game Types',
      games: 'Games',
    },
    actions: {
      createGameType: 'Create Game Type',
      createGame: 'Create game',
      editGameType: 'Edit Game Type',
      editGame: 'Edit game',
      changeStatus: 'Change status',
      applyGameTypeFilters: 'Apply Game Type filters',
      applyGameFilters: 'Apply Game filters',
      searchGameTypes: 'Search Game Types',
      previousGameTypes: 'Previous',
      nextGameTypes: 'Next',
    },
    fields: {
      code: 'Code',
      name: 'Display name',
      gameType: 'Game type',
      gameTypeCode: 'Game Type code',
      gameTypeName: 'Game Type name',
      gameCode: 'Game code',
      gameName: 'Game name',
      status: 'Status',
      effectiveStatus: 'Effective status',
      proposedStatus: 'Proposed status',
      dependencies: 'Dependency blockers',
      createdAt: 'Created',
      updatedAt: 'Updated',
      reason: 'Reason',
    },
    filters: {
      gameTypeCode: 'Game Type code filter',
      gameTypeSearch: 'Search Game Type code',
      gameCode: 'Game code filter',
      gameType: 'Game type filter',
      name: 'Display name filter',
      status: 'Status filter',
    },
    forms: {
      gameTypeCreate: 'Create Game Type',
      gameTypeEdit: 'Edit Game Type',
      gameCreate: 'Create game',
      gameEdit: 'Edit game',
      normalizedCode: 'Normalized code sent to the API',
      selectGameType: 'Select a Game Type',
      gameTypeResults: 'Showing {{shown}} of {{total}} · Page {{page}} of {{pages}}',
    },
    validation: {
      gameTypeCode: 'Use 2–32 letters, numbers, underscores, or hyphens.',
      gameCode: 'Use 2–64 letters, numbers, underscores, or hyphens.',
      gameTypeRequired: 'Select a Game Type.',
      nameRequired: 'Display name is required.',
      nameMaximum: 'Display name must be 200 characters or fewer.',
      reasonRequired: 'Reason is required.',
      reasonMaximum: 'Reason must be 2,000 characters or fewer.',
    },
    status: {
      title: 'Change lifecycle status',
      impact: '{{code}}: {{current}} → {{next}}',
      dependencies: 'The API runs dependency checks before applying this lifecycle change.',
      blockers: 'Current blockers: {{blockers}}',
      closedTerminal: 'CLOSED is terminal and cannot be reopened.',
    },
    tables: {
      gameTypes: 'Game Types table',
      games: 'Games table',
    },
    detail: {
      overview: 'Catalog overview',
      effectiveValue: 'Effective: {{status}}',
      noDependencies: 'No dependency blockers',
      related: 'Related operations',
      partnerAvailability: 'Partner game availability',
      choosePartnerAvailability: 'Choose a Partner to manage this Game’s availability',
    },
    empty: {
      gameTypes: 'No Game Types match these filters.',
      games: 'No Games match these filters.',
    },
    errors: {
      loadGameTypes: 'Unable to load Game Types.',
      loadGames: 'Unable to load Games.',
      detail: 'Unable to load this catalog record.',
      mutation: 'Unable to save this catalog record.',
      lifecycle: 'Unable to change lifecycle status.',
    },
    partner: {
      title: 'Partner game availability',
      subtitle: 'Partner {{partnerId}} · Scoped availability and maximum play limits.',
      tableLabel: 'Partner games table',
      resultCount: '{{count}} configured games',
      empty: 'No Games are configured for this Partner.',
      actions: {
        backToPartner: 'Back to Partner',
        configure: 'Configure game',
        edit: 'Edit availability',
        save: 'Save availability',
        changeStatus: 'Change availability status',
        searchGames: 'Search Games',
      },
      fields: {
        game: 'Game',
        gameSearch: 'Search Game code',
        currency: 'Locked currency',
        maximumPlayAmount: 'Maximum play amount',
        effectiveStatus: 'Effective status',
      },
      dialog: {
        createTitle: 'Configure Partner game availability',
        editTitle: 'Edit Partner game availability',
        statusTitle: 'Change Partner game status',
        createDescription:
          'The API creates this configuration as SUSPENDED and derives currency from the locked Partner default.',
        editDescription:
          'Review the current locked currency and maximum play amount before saving.',
        statusDescription:
          'Activation revalidates the Game, Game Type, Partner, and locked default currency.',
        lockedCurrency: 'Current locked currency',
        gameResults: 'Showing {{shown}} of {{total}} matching active Games',
        amountImpact: 'Maximum play amount: {{current}} → {{next}}',
        statusImpact: 'Availability status: {{current}} → {{next}}',
      },
      validation: {
        gameRequired: 'Select a Game.',
        amountRequired: 'Maximum play amount is required.',
        amountInvalid: 'Enter a positive amount with no more than 14 digits and 6 decimals.',
      },
      errors: {
        load: 'Unable to load Partner game availability.',
        loadGames: 'Unable to load active Games.',
        mutation: 'Unable to save Partner game availability.',
        currencyConflict:
          'Partner default currency changed. Suspend the assignment before refreshing its locked currency.',
        conflict: 'This Partner game change conflicts with its current state or dependencies.',
        notFound: 'This Partner game configuration is no longer available.',
        unavailable: 'This Partner game is not available for the requested operation.',
        gameNotFound: 'The selected Game is no longer available.',
        partnerNotFound: 'The selected Partner is no longer available.',
      },
    },
  },
  commissionRules: {
    title: 'Commission Rules',
    subtitle: 'Review immutable, versioned commission configurations and lifecycle state.',
    empty: 'No Commission Rules match these filters.',
    gridLabel: 'Commission Rules table',
    actions: {
      new: 'New rule version',
      preview: 'Preview calculation',
      create: 'Create rule',
      reset: 'Reset form',
      publish: 'Publish rule',
      cancel: 'Cancel rule',
      back: 'Back to Commission Rules',
    },
    fields: {
      version: 'Version',
      versionValue: 'Version {{version}}',
      scope: 'Scope',
      partnerId: 'Partner ID',
      gameTypeId: 'Game Type ID',
      gameId: 'Game ID',
      game: 'Game',
      activationMode: 'Activation mode',
      agentRatePercent: 'Agent rate (%)',
      affiliateRatePercent: 'Affiliate rate (%)',
      effectiveFrom: 'Effective from',
      effectiveTo: 'Effective to',
      lifecycle: 'Lifecycle',
      phase: 'Phase',
      effective: 'Effective now',
      createdAt: 'Created',
      createdBy: 'Creator',
      publishedAt: 'Published',
      approvedBy: 'Approver',
      cancelledAt: 'Cancelled',
      cancelledBy: 'Cancelled by',
      cancellationReason: 'Cancellation reason',
      sampleActualPlayAmount: 'Play amount',
      sampleCurrencyCode: 'Sample currency',
      actualPlayAmount: 'Actual play amount',
      agentCommissionUnrounded: 'Agent commission (unrounded)',
      affiliateCommissionUnrounded: 'Affiliate commission (unrounded)',
      agentCommission: 'Agent commission',
      affiliateCommission: 'Affiliate commission',
      totalCommission: 'Total commission',
      reason: 'Reason',
    },
    filters: {
      all: 'All',
      effectiveFrom: 'Effective from filter',
      effectiveTo: 'Effective to filter',
    },
    wizard: {
      title: 'Create Commission Rule version',
      subtitle: 'Preview the server-normalized configuration before creating an immutable draft.',
      impact:
        'A new immutable version may affect future commission calculations. Existing transaction snapshots never change.',
    },
    preview: {
      title: 'Calculation preview',
      serverValues: 'Exact decimal strings returned by the server; currencies are never combined.',
      agentFormula: '{{amount}} × {{rate}}% = agent commission before rounding',
      affiliateFormula: '{{amount}} × {{rate}}% = affiliate commission before rounding',
      impact: 'This configuration affects {{count}} Partner(s).',
      normalization: 'Preview normalization',
      stale: 'The form changed. Preview again before creation.',
    },
    configuration: {
      scope: 'Normalized scope',
      partnerId: 'Normalized Partner ID',
      gameId: 'Normalized Game ID',
      activationMode: 'Normalized activation mode',
      effectiveFrom: 'Normalized effective from',
      effectiveTo: 'Normalized effective to',
      agentRatePercent: 'Normalized Agent rate',
      affiliateRatePercent: 'Normalized Affiliate rate',
    },
    notices: {
      overlap: 'The effective period overlaps another applicable Commission Rule.',
      partnerImpact: 'Review the affected Partner count before creating this version.',
      gameUnavailable: 'The selected Game is not available for this Commission Rule.',
      rateTotalExceeded: 'The combined commission rate is not permitted.',
      warningFallback: 'The server returned a Commission Rule warning. Review the configuration.',
      blockerFallback:
        'The server blocked this configuration. Review the fields or contact an administrator.',
    },
    lifecycle: {
      publishTitle: 'Publish Commission Rule',
      cancelTitle: 'Cancel Commission Rule',
      publishImpact:
        '{{id}} will change from {{current}} to {{next}} and may affect future calculations.',
      cancelImpact:
        '{{id}} will change from {{current}} to {{next}}. Active and expired rules cannot be cancelled.',
    },
    detail: {
      immutable: 'Existing versions are immutable. No edit or delete action is available.',
      configuration: 'Rule configuration and lifecycle',
      dependencies: 'Dependency blockers',
      noDependencies: 'No dependency blockers were reported.',
    },
    dependencies: {
      referencedByTransactions: 'Referenced by historical transaction snapshots.',
      fallback: 'A server-reported dependency prevents this lifecycle action.',
    },
    validation: {
      required: 'This field is required.',
      rate: 'Enter a percentage from 0 through 100 with up to 6 decimals.',
      money: 'Enter a positive amount with up to 6 decimals.',
      currency: 'Enter a three-letter uppercase currency code.',
      reasonMaximum: 'Reason must be 2,000 characters or fewer.',
    },
    errors: {
      load: 'Unable to load Commission Rules.',
      detail: 'Unable to load this Commission Rule.',
      preview: 'Unable to preview this Commission Rule.',
      create: 'Unable to create this Commission Rule.',
      lifecycle: 'Unable to change this Commission Rule lifecycle.',
      stalePreview: 'The preview is stale. Preview the current fields again.',
      notFound: 'This Commission Rule is no longer available.',
    },
  },
  wallets: {
    title: 'Affiliate Wallets',
    subtitle: 'Inspect affiliate balances, wallet status, and balance movement history.',
    empty: 'No Affiliate Wallets match these filters.',
    results: 'Wallet results',
    reconciliationWarning:
      'This wallet is not reconciled with its immutable ledger. Investigate before taking financial action.',
    actions: {
      suspendWallet: 'Suspend wallet',
      reactivateWallet: 'Reactivate wallet',
      createAdjustment: 'Create adjustment',
    },
    fields: {
      walletId: 'Wallet ID',
      partner: 'Partner',
      partnerId: 'Partner ID',
      agent: 'Agent',
      agentId: 'Agent ID',
      member: 'Member',
      memberId: 'Member ID',
      username: 'Username',
      partnerUserId: 'Partner user ID',
      currency: 'Currency',
      status: 'Status',
      available: 'Available',
      reserved: 'Reserved',
      total: 'Total',
      lifetimeEarned: 'Lifetime earned',
      lifetimeReversed: 'Lifetime reversed',
      lifetimeAdjusted: 'Lifetime adjusted',
      lifetimeWithdrawn: 'Lifetime withdrawn',
      version: 'Version',
      entries: 'Ledger entries',
      withdrawals: 'Withdrawals',
      lastEntryAt: 'Last entry',
      reconciliation: 'Reconciliation',
      entryId: 'Entry ID',
      entryType: 'Entry type',
      amount: 'Amount',
      availableBefore: 'Available before',
      availableDelta: 'Available delta',
      availableAfter: 'Available after',
      reservedBefore: 'Reserved before',
      reservedDelta: 'Reserved delta',
      reservedAfter: 'Reserved after',
      sources: 'Linked sources',
      transactionId: 'Transaction ID',
      withdrawalId: 'Withdrawal ID',
      adjustmentId: 'Adjustment ID',
      effectiveAt: 'Effective at',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
    },
    filters: {
      all: 'All',
      minimumAvailable: 'Minimum available',
      maximumAvailable: 'Maximum available',
      minimumReserved: 'Minimum reserved',
      maximumReserved: 'Maximum reserved',
      minimumAmount: 'Minimum amount',
      maximumAmount: 'Maximum amount',
      effectiveFrom: 'Effective from',
      effectiveTo: 'Effective to',
      sort: 'Sort',
      apply: 'Apply filters',
    },
    pagination: { previous: 'Previous', next: 'Next' },
    detail: {
      tabsLabel: 'Wallet detail sections',
      overview: 'Overview',
      ledger: 'Ledger',
      related: 'Related records',
      balances: 'Balances and lifetime totals',
      identity: 'Wallet identity and reconciliation',
    },
    ledger: {
      empty: 'No immutable wallet entries match these filters.',
      results: 'Wallet ledger results',
    },
    chart: {
      title: 'Available-balance history',
      titleScoped: 'Balance trend — {{shown}} of {{total}} entries',
      available: 'Available balance',
      reserved: 'Reserved balance',
      period: 'Effective time',
      accessibleLabel: 'Display-only balance history',
      disclaimer:
        'Display only — derived from the immutable entries shown and never used as an accounting source.',
    },
    status: {
      suspendImpact:
        'Wallet {{wallet}}. Current status: {{current}}. Proposed status: {{proposed}}. Suspending this wallet blocks new withdrawals but does not block mandatory earn reversal.',
      reactivateImpact:
        'Wallet {{wallet}}. Current status: {{current}}. Proposed status: {{proposed}}. Reactivating this wallet allows new withdrawal requests again. Existing ledger entries remain immutable.',
    },
    adjustment: {
      title: 'Create wallet adjustment',
      description:
        'Preview a financial adjustment for wallet {{wallet}}. No balance changes until a separate approval is granted and executed.',
      signedAmount: 'Signed amount',
      amountHelp: 'Enter a non-zero signed decimal with up to 6 decimal places.',
      reasonCode: 'Reason code',
      evidenceReference: 'Evidence reference',
      reason: 'Reason',
      impact: 'Server preview and financial impact',
      availableBefore: 'Available before',
      delta: 'Adjustment delta',
      availableAfter: 'Available after',
      reservedAndTotalAfter: 'Reserved / total after',
      notProvided: 'Reserved/total after not provided by preview API.',
      approvalRequired:
        'Submitting creates a PENDING approval request; it does not post the adjustment.',
      confirmImpact: 'I confirm the target and financial impact shown above',
      preview: 'Preview adjustment',
      submit: 'Submit for approval',
      approvalCreated: 'Approval request created with status {{status}}.',
      viewApproval: 'View approval request',
      errors: {
        preview: 'Unable to preview this wallet adjustment.',
        submit: 'Unable to submit this wallet adjustment.',
      },
    },
    links: {
      transaction: 'Transaction',
      withdrawal: 'Withdrawal',
      commissionLedger: 'Commission ledger',
      walletAdjustment: 'Wallet adjustment',
      transactions: 'Linked transactions',
      withdrawals: 'Linked withdrawals',
      audit: 'Wallet audit activity',
    },
    errors: {
      list: 'Unable to load Affiliate Wallets.',
      detail: 'Unable to load this Affiliate Wallet.',
      entries: 'Unable to load immutable wallet entries.',
    },
  },
  withdrawals: {
    title: 'Affiliate Withdrawals',
    subtitle: 'Investigate withdrawal state, reserved funds, and masked Partner-credit evidence.',
    empty: 'No Affiliate Withdrawals match these filters.',
    results: 'Withdrawal results',
    serverOrderNote:
      'Results preserve server order. Use CREDIT_STATUS_UNKNOWN or MANUAL_REVIEW status filters to investigate exception queues.',
    exceptionAction:
      'Funds remain reserved. Review immutable events and delivery evidence before any controlled resolution.',
    receiptGap:
      'The current API does not provide a separate receipt field. Partner credit ID and CREDITED delivery status are the available credit evidence.',
    actions: {
      retry: 'Retry status check',
      resolve: 'Resolve withdrawal',
    },
    retry: {
      title: 'Retry withdrawal delivery',
      description:
        'Schedule controlled recovery against the immutable existing transfer. No replacement transfer ID is created.',
      transferId: 'Existing transfer ID',
      stateChange: 'Current state: {{status}} · proposed state: RETRY_PENDING',
      impact:
        'The service checks the existing transfer outcome before any credit retry. The browser does not calculate or mutate wallet balances.',
      reason: 'Reason',
      confirm: 'I confirm recovery must use the existing transfer ID',
      submit: 'Schedule retry',
      scheduled: 'Retry was scheduled with state RETRY_PENDING.',
      errors: { submit: 'Unable to schedule this Withdrawal retry.' },
    },
    resolution: {
      title: 'Resolve withdrawal',
      description:
        'Preview a controlled resolution for {{withdrawal}}. Submission creates an approval request and does not complete a wallet mutation.',
      resolution: 'Resolution',
      completeProof: 'COMPLETE requires proof that Partner credit exists.',
      releaseProof: 'RELEASE requires proof that Partner credit did not occur.',
      partnerCreditId: 'Partner credit ID',
      failureCode: 'Failure code',
      evidenceReference: 'Evidence reference',
      reason: 'Reason',
      impact: 'Resolution impact',
      amount: 'Reserved amount',
      proposedState: 'State after approval and execution',
      approvalRequired:
        'Submitting creates a PENDING approval request. Funds remain unchanged until approval and execution.',
      confirmImpact: 'I confirm the target, evidence, and proposed financial impact',
      preview: 'Preview resolution',
      submit: 'Submit for approval',
      approvalCreated:
        'Approval request created with status {{status}}. No wallet mutation is complete.',
      viewApproval: 'View approval request',
      errors: {
        preview: 'Unable to preview this Withdrawal resolution.',
        submit: 'Unable to submit this Withdrawal resolution.',
      },
    },
    fields: {
      partnerWithdrawalId: 'Partner withdrawal ID',
      internalId: 'Internal ID',
      transferId: 'Transfer ID',
      partnerCreditId: 'Masked Partner credit ID',
      user: 'User',
      partnerUserId: 'Partner user ID',
      partner: 'Partner',
      partnerId: 'Partner ID',
      agent: 'Agent',
      agentId: 'Agent ID',
      walletId: 'Wallet ID',
      memberId: 'Member ID',
      currency: 'Currency',
      amount: 'Amount',
      status: 'Status',
      exception: 'Exception',
      reservedAge: 'Reserved age',
      attempts: 'Attempts',
      lastResponse: 'Last masked Partner response',
      deliveryResult: 'Delivery result',
      failureCode: 'Failure code',
      failureDetail: 'Masked failure detail',
      minimumAmount: 'Minimum amount',
      maximumAmount: 'Maximum amount',
      createdFrom: 'Created from',
      createdTo: 'Created to',
      completedFrom: 'Completed from',
      completedTo: 'Completed to',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
      completedAt: 'Completed at',
      reservedAt: 'Reserved at',
      releasedAt: 'Released at',
      sort: 'Server sort',
      evidence: 'Evidence reference',
      latestAttempt: 'Latest masked delivery summary',
      version: 'Version',
      events: 'State events',
      idempotencyId: 'Masked idempotency record ID',
      idempotencyKey: 'Masked idempotency key',
      idempotencyState: 'Idempotency state',
      responseStatus: 'Response status',
      expiresAt: 'Expires at',
    },
    pagination: { previous: 'Previous', next: 'Next', loadMore: 'Load more' },
    detail: {
      tabsLabel: 'Withdrawal investigation sections',
      overview: 'Overview',
      timeline: 'Timeline',
      attempts: 'Delivery attempts',
      identity: 'Withdrawal identity and funds',
      operational: 'Masked operational evidence',
      idempotency: 'Idempotency metadata',
      walletPostings: 'Immutable wallet postings',
    },
    timeline: {
      empty: 'No immutable state events are available.',
      label: 'Immutable withdrawal timeline',
    },
    attempts: {
      empty: 'No masked delivery attempts are available.',
      label: 'Masked delivery attempts',
      attemptId: 'Attempt ID',
      attemptNumber: 'Attempt',
      operation: 'Operation',
      result: 'Result',
      httpStatus: 'HTTP status',
      retryable: 'Retryable',
      partnerCreditId: 'Masked Partner credit ID',
      error: 'Masked error summary',
      transferId: 'Transfer ID',
      integrationVersion: 'Integration version',
      endpoint: 'Credential-free endpoint origin',
      startedAt: 'Started at',
      completedAt: 'Completed at',
    },
    links: {
      member: 'Member',
      partner: 'Partner',
      agent: 'Agent',
      wallet: 'Wallet',
      ledger: 'Wallet ledger entry',
      audit: 'Withdrawal audit activity',
    },
    errors: {
      list: 'Unable to load Affiliate Withdrawals.',
      detail: 'Unable to load this Affiliate Withdrawal.',
      events: 'Unable to load immutable Withdrawal events.',
      attempts: 'Unable to load masked delivery attempts.',
    },
  },
  withdrawalControls: {
    title: 'Partner Withdrawal Controls',
    subtitle: 'Review withdrawal policy and historical velocity rules for Partner {{partnerId}}.',
    backToPartner: 'Back to Partner',
    actions: {
      createPolicy: 'Create policy',
      activatePolicy: 'Activate policy',
      suspendPolicy: 'Suspend policy',
    },
    fields: {
      currency: 'Currency',
      currencyFilter: 'Currency filter',
      status: 'Status',
      statusFilter: 'Status filter',
      actions: 'Actions',
      requestLimits: 'Minimum / maximum request',
      minimumAmount: 'Minimum amount',
      maximumAmount: 'Maximum amount',
      dailyAmountLimit: 'Daily amount limit',
      dailyCountLimit: 'Daily count limit',
      dimension: 'Dimension',
      dimensionFilter: 'Dimension filter',
      windowSeconds: 'Window seconds',
      maximumCount: 'Maximum count',
      reason: 'Reason',
    },
    filters: { all: 'All' },
    policy: {
      title: 'Withdrawal policies',
      gridLabel: 'Withdrawal policies table',
      listNote: 'Server-paged policies; the API defines no client-selectable sort order.',
      createTitle: 'Create withdrawal policy',
      editTitle: 'Edit withdrawal policy',
      activateTitle: 'Activate withdrawal policy',
      suspendTitle: 'Suspend withdrawal policy',
      description:
        'Policy limits are submitted as exact decimal strings. Currency is immutable after creation.',
      impact: 'Policy limits and impact',
      dailyLimit: 'Daily limit: {{amount}} across {{count}} requests',
      dailyLimitDisabled: 'Legacy daily limits are retained for history and are not enforced.',
      platformBehaviorTitle: 'Non-configurable platform behavior',
      synchronousDelivery:
        'Initial delivery: synchronous attempt. This is platform behavior and is not a field returned or changed by the withdrawal-policy API.',
      confirmActivation: 'I confirm these limits and the certified integration',
      suspendImpact: 'Suspending this policy blocks new withdrawals for this Partner currency.',
    },
    velocity: {
      title: 'Velocity rules',
      gridLabel: 'Withdrawal velocity rules table',
      listNote:
        'Historical rules are read-only; server-paged by currency and dimension with no client-selectable sort order.',
      createTitle: 'Create velocity rule',
      editTitle: 'Edit velocity rule',
      activateTitle: 'Activate velocity rule',
      suspendTitle: 'Suspend velocity rule',
      description:
        'Currency, dimension, and window are immutable after creation. At least one limit is required.',
      activateImpact: 'Activate the {{dimension}} velocity rule for enforcement.',
      suspendImpact:
        'Suspend the {{dimension}} rule. The server rejects removal of the last active required dimension while its currency policy is active.',
    },
    errors: {
      load: 'Unable to load withdrawal controls.',
      mutation: 'Unable to save this withdrawal-control change.',
    },
  },
  commissionLedger: {
    title: 'Commission Ledger',
    subtitle: 'Inspect calculated commission entries, adjustments, and settlement references.',
    empty: 'No Commission Ledger entries match these filters.',
    gridLabel: 'Commission Ledger table',
    compactResultsLabel: 'Commission Ledger compact results',
    actions: {
      createAdjustment: 'Create adjustment',
      applyFilters: 'Apply filters',
    },
    fields: {
      entryId: 'Entry ID',
      entryType: 'Entry type',
      signedAmount: 'Signed amount',
      currency: 'Currency',
      beneficiary: 'Beneficiary',
      beneficiaryType: 'Beneficiary type',
      beneficiaryId: 'Beneficiary ID',
      beneficiaryUsername: 'Beneficiary username',
      partner: 'Partner',
      partnerId: 'Partner ID',
      agent: 'Agent',
      agentId: 'Agent ID',
      transactionId: 'Transaction ID',
      partnerTransactionId: 'Partner transaction ID',
      effectiveAt: 'Effective at',
      createdAt: 'Created at',
      createdBy: 'Created by',
      reasonCode: 'Reason code',
      reason: 'Reason',
      calculationId: 'Calculation ID',
      reversalId: 'Reversal ID',
      sourceEntryId: 'Source entry ID',
      approvalRequestId: 'Approval request ID',
      adjustmentId: 'Adjustment ID',
      walletEntryId: 'Wallet entry ID',
      walletId: 'Wallet ID',
      walletEntryType: 'Wallet entry type',
      availableDelta: 'Available delta',
      availableAfter: 'Available after',
    },
    filters: {
      minimumAmount: 'Minimum signed amount',
      maximumAmount: 'Maximum signed amount',
      effectiveFrom: 'Effective from (inclusive)',
      effectiveTo: 'Effective to (exclusive)',
      sort: 'Descending sort timestamp',
      contractNote:
        'The API returns cursor pages in descending timestamp and entry-ID order. It does not provide an ascending order or total count.',
    },
    pagination: {
      previous: 'Previous page',
      next: 'Next page',
    },
    detail: {
      immutable: 'This entry is immutable. No edit or delete action is available.',
      overview: 'Ledger entry',
      source: 'Source references',
      walletPosting: 'Wallet posting',
    },
    adjustment: {
      title: 'Create commission adjustment',
      description:
        'DEV mock workflow only. This creates a mock approval request and never calls a backend adjustment endpoint.',
      unavailable:
        'Commission adjustment API is unavailable. Live mode remains read-only until a matching backend endpoint is approved.',
      signedAmountHelp: 'Use a non-zero signed decimal with up to 6 fractional digits.',
      evidenceReference: 'Evidence reference',
      impact: 'Financial impact preview',
      availableBefore: 'Available before',
      availableAfter: 'Available after',
      confirmImpact: 'I confirm the target and proposed financial impact',
      preview: 'Preview adjustment',
      submit: 'Submit mock approval',
      approvalCreated: 'Mock approval {{approvalId}} was created and remains pending.',
    },
    errors: {
      load: 'Unable to load Commission Ledger entries.',
      detail: 'Unable to load this Commission Ledger entry.',
      preview: 'Unable to preview this mock commission adjustment.',
      submit: 'Unable to submit this mock commission adjustment.',
    },
  },
  partners: {
    title: 'Partners',
    subtitle: 'Manage Partner lifecycle, API credentials, and network access.',
    gridLabel: 'Partners table',
    empty: 'No Partners match these filters.',
    actions: {
      create: 'Create partner',
      edit: 'Edit partner',
      changeStatus: 'Change status',
      applyFilters: 'Apply filters',
    },
    fields: {
      code: 'Partner code',
      name: 'Partner name',
      affiliateCommissionExpiry: 'Affiliate commission expiry (days)',
      status: 'Status',
      proposedStatus: 'Proposed status',
      currency: 'Currency',
      timezone: 'Timezone',
      usernameNormalization: 'Username normalization',
      requestRateLimit: 'Request limit / minute',
      withdrawalRateLimit: 'Withdrawal limit / minute',
      timestampTolerance: 'Timestamp tolerance (seconds)',
      createdAt: 'Created',
      updatedAt: 'Updated',
      reason: 'Reason',
    },
    filters: {
      code: 'Partner code',
      name: 'Partner name',
      status: 'Status',
      currency: 'Currency',
    },
    validation: {
      codeRequired: 'Partner code is required',
      codeInvalid: 'Use 2–32 letters, numbers, underscores, or hyphens',
      nameRequired: 'Partner name is required',
      currencyRequired: 'Currency is required',
      currencyInvalid: 'Use a three-letter currency code',
      timezoneRequired: 'Timezone is required',
      reasonRequired: 'Reason is required',
      positive: 'Enter a value of at least 1',
      nonNegative: 'Enter zero or a positive value',
      toleranceMaximum: 'Maximum tolerance is 3600 seconds',
      expiryRange: 'Enter 1–365 days or leave blank to disable expiry',
    },
    form: {
      createTitle: 'Create Partner',
      editTitle: 'Edit Partner',
      affiliateCommissionExpiryHelp:
        'Applies to future commission lots. Leave blank if they should never expire.',
    },
    errors: {
      load: 'Unable to load Partners.',
      detail: 'Unable to load this Partner.',
      mutation: 'Unable to save this Partner change.',
    },
    status: {
      title: 'Change Partner status',
      impact: '{{code}} will change from {{current}} to {{next}}.',
      closedTerminal: 'CLOSED is terminal and cannot be reversed.',
    },
    detail: {
      tabsLabel: 'Partner detail sections',
      overview: 'Overview',
      expiryDays: '{{count}} days',
      neverExpires: 'Never expires',
      related: 'Related operations',
      gameAvailability: 'Game availability',
      withdrawalControls: 'Withdrawal controls',
      partnerCredit: 'Partner credit',
      audit: 'Audit',
    },
    credentials: {
      title: 'Credentials',
      safeMetadata: 'Only safe credential metadata is retained and displayed.',
      empty: 'No API credentials have been issued.',
      loadError: 'Unable to load Partner credentials.',
      mutationError: 'Unable to update this credential.',
      issue: 'Issue credential',
      issueTitle: 'Issue API credential',
      issueVerb: 'Issue',
      secretWarning: 'The secret is displayed once after a successful issue.',
      keyId: 'Key ID',
      validFrom: 'Valid from',
      expiresAt: 'Expires at',
      expiresAtOptional: 'Expires at (optional ISO timestamp)',
      lastUsedAt: 'Last used',
      actions: 'Actions',
      rotate: 'Rotate',
      expiry: 'Change expiry',
      revoke: 'Revoke',
      rotateTitle: 'Rotate credential',
      rotateImpact: 'A new credential will be issued for {{keyId}}. The source remains active.',
      revokeTitle: 'Revoke credential',
      revokeImpact: '{{keyId}} will be permanently revoked.',
      expiryTitle: 'Change credential expiry',
      expiryRequired: 'Expiry is required',
      oneTimeTitle: 'One-time credential secret',
      oneTimeWarning: 'Copy this secret now. It cannot be retrieved again.',
      copyError: 'The secret could not be copied. Select and copy it manually, then acknowledge.',
      discarded: 'I intentionally discarded this secret',
      copyClose: 'Copy and close',
      acknowledgeClose: 'Acknowledge and close',
    },
    ip: {
      title: 'IP Allowlist',
      unrestrictedWarning: 'No active entries means future Partner API access is unrestricted.',
      empty: 'No IP allowlist entries are configured.',
      loadError: 'Unable to load the IP allowlist.',
      mutationError: 'Unable to update the IP allowlist.',
      add: 'Add network',
      network: 'Network',
      description: 'Description',
      actions: 'Actions',
      createTitle: 'Add IP allowlist entry',
      editTitle: 'Edit IP allowlist entry',
      networkRequired: 'Network is required',
      descriptionRequired: 'Description is required',
      statusTitle: 'Change IP allowlist status',
      statusImpact: '{{network}} will change from {{current}} to {{next}}.',
    },
  },
  agents: {
    title: 'Agents',
    subtitle: 'Manage Agent lifecycle, member assignment context, and default uplines.',
    empty: 'No Agents match these filters.',
    gridLabel: 'Agents table',
    actions: {
      create: 'Create agent',
      edit: 'Edit agent',
      changeStatus: 'Change status',
      changeUpline: 'Change default upline',
      createHouse: 'Create house member',
      applyFilters: 'Apply filters',
    },
    fields: {
      partner: 'Partner',
      partnerId: 'Partner ID',
      code: 'Agent code',
      name: 'Agent name',
      contact: 'Contact JSON',
      status: 'Status',
      proposedStatus: 'Proposed status',
      currency: 'Default currency',
      userCount: 'Users',
      activeUserCount: 'Active users',
      defaultUpline: 'Default upline',
      memberId: 'Member ID',
      username: 'Username',
      partnerUserId: 'Partner user ID (optional)',
      createdAt: 'Created',
      updatedAt: 'Last updated',
      reason: 'Reason',
    },
    filters: {
      partnerId: 'Partner ID',
      code: 'Agent code',
      name: 'Agent name',
      status: 'Status',
    },
    validation: {
      partnerRequired: 'Partner ID is required',
      codeRequired: 'Agent code is required',
      codeInvalid: 'Use 2–32 letters, numbers, underscores, or hyphens',
      nameRequired: 'Agent name is required',
      contactInvalid: 'Contact must be a JSON object',
      memberRequired: 'Member ID is required',
      usernameRequired: 'Username is required',
      reasonRequired: 'Reason is required',
    },
    form: {
      createTitle: 'Create Agent',
      editTitle: 'Edit Agent',
      contactHelp: 'Enter a JSON object using only approved contact attributes.',
    },
    errors: {
      load: 'Unable to load Agents.',
      detail: 'Unable to load this Agent.',
      mutation: 'Unable to save this Agent change.',
      houseMember: 'Unable to create the house member.',
    },
    status: {
      title: 'Change Agent status',
      impact:
        '{{code}} will change from {{current}} to {{next}}. This Agent currently has {{activeUsers}} active users.',
      activeUserBlock: 'Deactivation requires zero active NORMAL users.',
      closedTerminal: 'CLOSED is terminal and cannot be reversed.',
    },
    upline: {
      title: 'Change default upline',
      confirmation: 'Confirm the selected Member ID and reason before submission.',
    },
    house: {
      title: 'Create house member',
      impact:
        'This creates an ACTIVE HOUSE member for {{code}} and does not change the default upline.',
    },
    detail: {
      overview: 'Overview',
      related: 'Related records',
      partner: 'Partner',
      members: 'Members',
      transactions: 'Transactions',
      commissionReports: 'Commission reports',
    },
  },
  members: {
    title: 'Members & Referrals',
    subtitle: 'Trace member profiles, referral relationships, and affiliate attribution.',
    empty: 'No Members match these filters.',
    gridLabel: 'Members table',
    contractNote:
      'Activity, play amount, generated commission, and withdrawal status are not provided by the Members API.',
    actions: {
      applyFilters: 'Apply filters',
      changeAssignment: 'Change assignment',
      rotateReferral: 'Rotate referral code',
      changeStatus: 'Change status',
      bindPartnerUser: 'Bind partner user ID',
      preview: 'Preview change',
      submitAssignment: 'Submit change',
      cancelChangeSet: 'Cancel change set',
    },
    fields: {
      username: 'Username',
      partnerUserId: 'Partner user ID',
      partner: 'Partner',
      agent: 'Agent',
      upline: 'Upline',
      directUpline: 'Direct upline',
      referralCode: 'Referral code',
      status: 'Status',
      proposedStatus: 'Proposed status',
      type: 'Member type',
      registeredAt: 'Registered',
      downlines: 'Direct downlines',
      wallet: 'Available wallet balance',
      walletStatus: 'Wallet status',
      available: 'Available balance',
      reserved: 'Reserved balance',
      assignmentType: 'Assignment type',
      agentDefaultUpline: 'Agent default upline',
      effectiveAt: 'Effective at',
      reason: 'Reason',
      createdAt: 'Created',
      updatedAt: 'Last updated',
    },
    filters: {
      username: 'Username',
      partnerId: 'Partner ID',
      agentId: 'Agent ID',
      uplineId: 'Upline Member ID',
      partnerUserId: 'Partner user ID',
      referralCode: 'Referral code',
      type: 'Member type',
      status: 'Status',
    },
    validation: {
      reasonRequired: 'Reason is required',
      partnerUserRequired: 'Partner user ID is required',
      agentRequired: 'Agent is required',
      uplineRequired: 'Upline is required',
      effectiveRequired: 'Effective time is required',
      futureRequired: 'Effective time must be in the future',
    },
    errors: {
      load: 'Unable to load Members.',
      detail: 'Unable to load this Member.',
      downlines: 'Unable to load direct downlines.',
      assignments: 'Unable to load assignment history.',
      mutation: 'Unable to save this Member change.',
      binding: 'Unable to bind this HOUSE identity.',
      preview: 'Unable to preview this assignment change.',
      assignment: 'Unable to create this assignment change set.',
      changeSet: 'Unable to load this assignment change set.',
      cancelChangeSet: 'Unable to cancel this assignment change set.',
    },
    detail: {
      tabsLabel: 'Member detail sections',
      overview: 'Overview',
      downlines: 'Direct downlines',
      assignments: 'Assignment history',
      related: 'Related records',
      transactions: 'Transactions',
      wallets: 'Wallets',
      withdrawals: 'Withdrawals',
      audit: 'Audit',
    },
    binding: {
      title: 'Bind HOUSE identity',
      impact:
        'This permanently binds {{username}} to one NewPortal user ID. Verify the cross-system identity before saving.',
    },
    downlines: {
      directOnly: 'Only direct downlines earn affiliate commission.',
      deeper: 'Deeper referral levels are non-commissionable.',
      empty: 'This Member has no current direct downlines.',
      gridLabel: 'Direct downlines table',
    },
    assignments: {
      empty: 'No assignment history is available.',
      timelineNote: 'History is effective-dated and shown with the original timestamp offset.',
      changeSet: 'Open change set',
    },
    assignment: {
      title: 'Change Member assignment',
      impact:
        '{{member}} will move to the proposed Agent and direct upline at the future effective time.',
      futureOnly: 'Assignment changes must take effect in the future.',
      previewHash: 'Preview hash',
      previewChanges: 'Server preview changes',
      previewChangesLabel: 'Assignment preview changes',
      memberId: 'Member ID',
      beforeAgent: 'Before · Agent ID',
      beforeUpline: 'Before · Upline Member ID',
      afterAgent: 'After · Agent ID',
      afterUpline: 'After · Upline Member ID',
      previewBound:
        'This opaque hash binds the normalized inputs to current server dependency state.',
      confirm: 'I explicitly confirm this assignment change and its displayed impact.',
      expired:
        'Preview expired because the inputs changed. Preview the change again before submission.',
    },
    referral: {
      title: 'Rotate referral code',
      impact: '{{code}} will be permanently revoked and replaced.',
      relationships: 'Existing referral relationships remain unchanged.',
    },
    status: {
      title: 'Change Member status',
      impact: '{{username}} will change from {{current}} to {{next}}.',
      closedTerminal: 'CLOSED is terminal and cannot be reversed.',
    },
    changeSet: {
      title: 'Assignment change set {{id}}',
      status: 'Status',
      mode: 'Mode',
      createdAt: 'Created',
      items: 'Member changes',
      cancelTitle: 'Cancel assignment change set',
      cancelImpact:
        '{{id}} will change from {{current}} to {{next}} and restore predecessor periods exactly.',
      elapsed: 'This change set can no longer be cancelled because its effective time has passed.',
    },
  },
  adminUsers: {
    title: 'Backoffice Users',
    subtitle: 'Manage Backoffice identities, roles, scopes, and permission profiles.',
    empty: 'No Backoffice Users match these filters.',
    gridLabel: 'Backoffice users table',
    actions: {
      invite: 'Invite user',
      edit: 'Edit user',
      editAccess: 'Edit access',
      changeStatus: 'Change status',
    },
    fields: {
      subject: 'IAM subject',
      username: 'Username',
      displayName: 'Display name',
      email: 'Email',
      role: 'Role',
      status: 'Status',
      proposedStatus: 'Proposed status',
      partnerId: 'Partner ID',
      agentId: 'Agent ID',
      profile: 'Permission profile',
      profileId: 'Permission profile ID',
      permissions: 'Permissions',
      assignedUsers: 'Assigned users',
      lastLogin: 'Last login',
      updatedAt: 'Updated',
      reason: 'Reason',
      actions: 'Actions',
    },
    access: {
      title: 'Edit user access',
      impact: '{{name}} currently has role {{role}}. Confirm the proposed role and scope.',
      selfBlocked:
        'You cannot change your own role or scope. You also cannot change your own status.',
      roleManaged: 'Defined by the Super Admin role',
      profileManaged: 'Defined by the assigned permission profile',
      roleGrants: 'Role grants',
      roleGrantsUnavailable:
        'Role grants are resolved by the server and are not listed by this API contract.',
      profileGrants: 'Assigned profile grants',
      noProfileGrants: 'No permission profile is assigned.',
    },
    status: {
      impact: '{{name}} is currently {{status}}. This changes Backoffice access immediately.',
    },
    profiles: {
      title: 'Permission Profiles',
      subtitle: 'Manage Agent-scoped, delegable permission sets.',
      empty: 'No permission profiles match these filters.',
      gridLabel: 'Permission profiles table',
      create: 'Create profile',
      edit: 'Edit permission profile',
      catalog: 'Permission catalog by domain',
      delegableOnly: 'Only permissions marked delegable by the API can be assigned.',
      sensitive: 'Sensitive',
      notDelegable: 'Not delegable',
      affected: '{{count}} affected users',
      statusImpact: 'The profile will change from {{current}} to {{next}}.',
      domains: {
        audit: 'Audit',
        member: 'Members',
        transaction: 'Transactions',
        wallet: 'Wallets',
      },
    },
    security: {
      title: 'Security',
      resetMfa: 'Reset MFA (mock)',
      mockDescription:
        'There is no MFA-reset API in the current contract. This is a mock-only affordance for demonstration and never sends a request.',
      mockAcknowledged: 'Mock action recorded locally. No request was sent to any service.',
      identityProviderManaged:
        'MFA is managed by your identity provider and cannot be reset from this console.',
    },
    validation: { required: 'This field is required' },
    errors: {
      load: 'Unable to load Backoffice Users.',
      detail: 'Unable to load this Backoffice User.',
      mutation: 'Unable to save this Backoffice User change.',
      profiles: 'Unable to load permission profiles.',
      profileDetail: 'Unable to load the assigned permission profile.',
      profileMutation: 'Unable to save this permission profile change.',
    },
  },
  transactions: {
    title: 'Transactions',
    subtitle: 'Investigate immutable transaction, attribution, calculation, and posting evidence.',
    empty: 'No transactions match these filters.',
    gridLabel: 'Transaction results table',
    compactResultsLabel: 'Transaction results',
    serverOrder: 'Results retain server order. Pagination uses opaque cursors only.',
    actions: { applyFilters: 'Apply filters' },
    pagination: { previous: 'Previous', next: 'Next' },
    errors: { list: 'Unable to load transactions.', detail: 'Unable to load this transaction.' },
    fields: {
      partner: 'Partner',
      partnerId: 'Partner ID',
      agentId: 'Agent ID',
      memberId: 'Member ID',
      username: 'Username',
      partnerUserId: 'Partner user ID',
      uplineMemberId: 'Upline Member ID',
      uplineUsername: 'Upline username',
      gameTypeId: 'Game Type ID',
      gameTypeCode: 'Game Type code',
      gameId: 'Game ID',
      gameCode: 'Game code',
      gameRound: 'Game round',
      transactionId: 'Transaction ID',
      partnerTransactionId: 'Partner transaction ID',
      outcome: 'Outcome',
      status: 'Status',
      reversalState: 'Reversal state',
      currency: 'Currency',
      errorCode: 'Error code',
      minimumAmount: 'Minimum play amount',
      maximumAmount: 'Maximum play amount',
      playFrom: 'Play from (RFC3339)',
      playTo: 'Play to (RFC3339)',
      receivedFrom: 'Received from (RFC3339)',
      receivedTo: 'Received to (RFC3339)',
      sort: 'Server sort',
      actualPlayAmount: 'Actual play amount',
      winLossAmount: 'Win/loss amount',
      playAt: 'Played at',
      receivedAt: 'Received at',
      rule: 'Commission rule',
      assignmentId: 'Assignment ID',
      agentFormula: 'Agent formula',
      affiliateFormula: 'Affiliate formula',
      totalCommission: 'Total commission',
      roundingMode: 'Rounding mode',
      calculatedAt: 'Calculated at',
      calculatedBy: 'Calculated by',
      trace: 'Safe trace',
      failureCode: 'Failure code',
      failureSummary: 'Safe failure summary',
      idempotencyId: 'Idempotency record ID',
      idempotencyKey: 'Idempotency key (irreversibly masked)',
      idempotencyState: 'Idempotency state',
      responseStatus: 'Response status',
      createdAt: 'Created at',
      completedAt: 'Completed at',
      expiresAt: 'Expires at',
      audit: 'Audit',
    },
    detail: {
      immutable: 'Read-only transaction investigation',
      requestId: 'Request ID',
      overview: 'Overview',
      relationshipSnapshot: 'Immutable relationship snapshot',
      unresolved: 'Relationship resolution unavailable',
      calculation: 'Commission calculation',
      noCalculation: 'No commission calculation exists for this transaction.',
      ruleVersion: 'Rule version {{version}}',
      ledgerSources: 'Ledger and wallet sources',
      ledgerEntry: 'Ledger entry · {{id}}',
      walletLink: 'Wallet · {{id}}',
      reversalChain: 'Reversal relationship',
      reversalContractNote:
        'The API provides the current transaction, one linked reversal, and related ledger sources; no multi-hop reversal history endpoint is available.',
      originalTransaction: 'Original transaction · {{id}}',
      notReversed: 'No linked reversal',
      requestTrace: 'Request and idempotency trace',
      safeMetadata:
        '{{count}} safe metadata fields retained by the API. Raw request payloads, headers, credentials, tokens, and hashes are never displayed.',
      partnerLink: 'Partner · {{name}}',
      agentLink: 'Agent · {{code}}',
      memberLink: 'Member · {{username}}',
      gameLink: 'Game · {{code}}',
      auditLink: 'Transaction audit trail',
    },
    reversal: {
      action: 'Manual reversal',
      title: 'Manual transaction reversal',
      description:
        'Create a DEV-only mock approval request for a compensating reversal. The original transaction remains immutable.',
      impact: 'Reversal impact',
      transactionState: 'Transaction state',
      signedImpact: 'Calculated signed impact',
      dualControl:
        'Dual control is recommended: a different authorized operator should decide this approval.',
      ineligible: 'Manual reversal requires a transaction that is calculated and not reversed.',
      confirmImpact: 'I confirm the displayed transaction state and financial impact.',
      submit: 'Submit mock approval',
      approvalCreated: 'Mock approval {{approvalId}} is pending a separate decision.',
      error: 'Unable to create the mock reversal approval.',
    },
  },
  transactionFailures: {
    title: 'Transaction Failure Queue',
    subtitle: 'Investigate grouped transaction submission failures and safe request evidence.',
    mockOnly:
      'This queue uses deterministic DEV mock records because the backend capability is unavailable.',
    empty: 'No failed submissions match this view.',
    tableLabel: 'Transaction failure queue',
    errors: {
      load: 'Unable to load the Transaction Failure Queue.',
    },
    fields: {
      errorCode: 'Error code',
      fieldErrors: 'Field errors',
      partner: 'Partner',
      firstOccurrence: 'First occurrence',
      lastOccurrence: 'Last occurrence',
      retryCount: 'Retry count',
      supportNotes: 'Support notes',
      requestId: 'Request ID',
    },
  },
  apiOperations: {
    title: 'API Operations',
    subtitle: 'Monitor request health and investigate failed Partner submissions.',
    errors: {
      load: 'Unable to load API Operations.',
    },
    filters: { endpoint: 'Endpoint', statusClass: 'Status class' },
    metrics: {
      label: 'API operations metrics',
      requestVolume: 'Request volume',
      errorRate: 'Error rate',
      p50Latency: 'P50 latency',
      p95Latency: 'P95 latency',
      p99Latency: 'P99 latency',
    },
    failures: {
      label: 'Failed submissions',
      status: 'Status',
      partner: 'Partner',
      errorCode: 'Error code',
      occurredAt: 'Occurred at',
      requestId: 'Request ID',
    },
  },
  reconciliation: {
    title: 'Reconciliation',
    subtitle:
      'Compare platform records with provider totals and identify reconciliation exceptions.',
    tabs: { label: 'Reconciliation views', runs: 'Runs', exceptions: 'Exceptions' },
    actions: {
      createRun: 'Create reconciliation run',
      queueRun: 'Queue run',
      resolveException: 'Resolve exception',
      acknowledgeException: 'Acknowledge legacy defect',
      viewExceptions: 'View reconciliation exceptions',
    },
    pagination: { previous: 'Previous', next: 'Next' },
    runsTable: 'Reconciliation runs',
    exceptionsTable: 'Reconciliation exceptions',
    compactRuns: 'Compact reconciliation runs',
    compactExceptions: 'Compact reconciliation exceptions',
    emptyRuns: 'No reconciliation runs match these filters.',
    emptyExceptions: 'No reconciliation exceptions match these filters.',
    allPartners: 'All Partners',
    createImpact: 'This queues an asynchronous financial comparison. It does not change balances.',
    resolveImpact:
      'Resolution rechecks evidence and records a clean exception. It never repairs financial state.',
    acknowledgeImpact:
      'Acknowledgement preserves the immutable history and records verified external evidence. It never changes a balance or ledger row.',
    confirmResolution: 'I confirm the evidence supports resolving this exception.',
    confirmAcknowledgement:
      'I confirm this is a verified historical chain defect and the current wallet is reconciled.',
    noResolution: 'This exception has not been resolved.',
    fields: {
      id: 'ID',
      type: 'Type',
      trigger: 'Trigger',
      phase: 'Phase',
      partner: 'Partner',
      partnerId: 'Partner ID',
      partnerIdOptional: 'Partner ID (optional)',
      checked: 'Checked',
      exceptions: 'Exceptions',
      requestedAt: 'Requested at',
      requestedBy: 'Requested by',
      processingStartedAt: 'Processing started at',
      lastHeartbeatAt: 'Last heartbeat at',
      completedAt: 'Completed at',
      openExceptions: 'Open exceptions',
      resolvedExceptions: 'Resolved exceptions',
      failureCode: 'Failure code',
      failureAttempts: 'Failure attempts',
      requestId: 'Request ID',
      status: 'Status',
      severity: 'Severity',
      entity: 'Entity',
      entityType: 'Entity type',
      entityId: 'Entity ID',
      lastDetected: 'Last detected',
      firstDetected: 'First detected',
      occurrences: 'Occurrences',
      firstRunId: 'First run ID',
      lastRunId: 'Latest run ID',
      from: 'From',
      to: 'To',
      reason: 'Reason',
      sourceTotal: 'Source total',
      internalTotal: 'Internal total',
      difference: 'Difference',
      currencyMismatch: 'Amount / currency mismatch',
      duplicateIds: 'Duplicate IDs',
      missingReceipts: 'Missing receipts',
      staleReservations: 'Stale reservations',
      evidence: 'Evidence',
      evidenceReference: 'Evidence reference',
      method: 'Resolution method',
      resolvedBy: 'Resolved by',
      resolvedAt: 'Resolved at',
      acknowledgedBy: 'Acknowledged by',
      acknowledgedAt: 'Acknowledged at',
      observationCount: 'Verified observations',
      evidenceFingerprint: 'Evidence fingerprint',
      lastObservedAt: 'Last observed at',
    },
    sections: {
      runSummary: 'Run summary',
      timeline: 'Timeline',
      exceptionSummary: 'Exception summary',
      mismatchEvidence: 'Immutable mismatch evidence',
      relatedRecords: 'Related records',
      resolution: 'Resolution',
      acknowledgement: 'Acknowledgement',
    },
    errors: {
      loadRuns: 'Unable to load reconciliation runs.',
      loadRun: 'Unable to load this reconciliation run.',
      createRun: 'Unable to queue the reconciliation run.',
      loadExceptions: 'Unable to load reconciliation exceptions.',
      loadException: 'Unable to load this reconciliation exception.',
      resolve: 'Unable to resolve the reconciliation exception.',
      acknowledge: 'Unable to acknowledge the reconciliation exception.',
      stillMismatched: 'Reconciliation is still mismatched. Review the evidence and retry.',
      combinedMaximum: 'Evidence and reason together must be 2,000 characters or fewer.',
    },
  },
  approvals: {
    title: 'Approvals',
    subtitle: 'Review governed change requests, decision context, and approval history.',
    table: 'Approval requests',
    compact: 'Compact approval requests',
    empty: 'No approval requests match these filters.',
    selfApprovalBlocked:
      'You requested this change, so the maker cannot approve their own request. Ask another checker to decide.',
    pagination: { previous: 'Previous', next: 'Next' },
    fields: {
      id: 'ID',
      partnerId: 'Partner ID',
      actionType: 'Action',
      entityType: 'Entity type',
      entityId: 'Entity ID',
      reason: 'Reason',
      evidenceReference: 'Evidence reference',
      status: 'Status',
      requestedBy: 'Requested by',
      requestedAt: 'Requested at',
      expiresAt: 'Expires at',
      decidedBy: 'Decided by',
      decidedAt: 'Decided at',
      decisionReason: 'Decision reason',
      executedAt: 'Executed at',
      executionReference: 'Execution reference',
      requestId: 'Request ID',
    },
    actions: {
      approve: 'Approve',
      reject: 'Reject',
      execute: 'Execute',
      noActionAvailable: 'No actions available · Status: {{status}}',
    },
    decision: {
      approveTitle: 'Approve request',
      rejectTitle: 'Reject request',
      reason: 'Decision reason',
      submit: 'Submit decision',
      decided: 'Decision recorded.',
    },
    execution: {
      title: 'Execute approval',
      impact:
        'Executing this approval performs the underlying money movement and cannot be undone.',
      confirmCheckbox: 'I confirm I want to execute this approved change.',
      confirm: 'Confirm execute',
      executing: 'Executing the approved change…',
      stale:
        'This approval preview is out of date. Refresh the latest approval before executing again.',
      success: 'The approved change was executed.',
      viewTarget: 'View target',
    },
    errors: {
      loadList: 'Unable to load approval requests.',
      loadDetail: 'Unable to load this approval request.',
      decision: 'Unable to record the decision.',
      execution: 'Unable to execute the approved change.',
    },
  },
  audit: {
    title: 'Audit',
    subtitle: 'Trace privileged actions, actors, affected resources, and request evidence.',
    table: 'Audit log',
    compact: 'Compact audit log',
    empty: 'No audit log entries match these filters.',
    allPartners: 'All Partners',
    noChangesRecorded: 'No before/after changes were recorded for this event.',
    noAdditionalFields: 'No additional fields were recorded for this event.',
    pagination: { previous: 'Previous', next: 'Next' },
    fields: {
      id: 'ID',
      action: 'Action',
      entity: 'Entity',
      entityType: 'Entity type',
      entityId: 'Entity ID',
      actor: 'Actor',
      principalActorId: 'Actor ID',
      principalRole: 'Role',
      serviceActorId: 'Service actor ID',
      partner: 'Partner',
      partnerId: 'Partner ID',
      agentId: 'Agent ID',
      requestId: 'Request ID',
      correlationId: 'Correlation ID',
      sourceIp: 'Source IP',
      occurredAt: 'Occurred at',
      reason: 'Reason',
      from: 'From',
      to: 'To',
      before: 'Before',
      after: 'After',
    },
    sections: {
      beforeAfter: 'Before / after',
      metadata: 'Additional metadata',
    },
    errors: {
      loadList: 'Unable to load audit log entries.',
      loadDetail: 'Unable to load this audit log entry.',
    },
  },
  securityEvents: {
    title: 'Security Events',
    subtitle: 'Review authentication, authorization, and suspicious security event signals.',
    table: 'Security events',
    compact: 'Compact security events',
    empty: 'No security events match these filters.',
    pagination: { previous: 'Previous', next: 'Next' },
    fields: {
      id: 'ID',
      category: 'Category',
      severity: 'Severity',
      eventCode: 'Event code',
      actor: 'Actor',
      principalActorId: 'Actor ID',
      principalRole: 'Role',
      serviceActorId: 'Service actor ID',
      partnerId: 'Partner ID',
      agentId: 'Agent ID',
      credentialActorId: 'Credential actor ID',
      requestId: 'Request ID',
      sourceIp: 'Source IP',
      occurredAt: 'Occurred at',
      from: 'From',
      to: 'To',
    },
    sections: {
      details: 'Additional details',
    },
    errors: {
      loadList: 'Unable to load security events.',
      loadDetail: 'Unable to load this security event.',
    },
  },
  profile: {
    title: 'Profile',
    fields: {
      name: 'Name',
      username: 'Username',
      email: 'Email',
      role: 'Role',
      authMode: 'Authentication mode',
      identityProviderMode: 'Cognito (identity provider)',
      partnerId: 'Partner ID',
      agentId: 'Agent ID',
      permissionProfileId: 'Permission profile ID',
      permissions: 'Effective permissions',
    },
    sessions: {
      title: 'Active sessions',
      mockNotice:
        'There is no session-listing API in the current contract. These sessions are deterministic Mock Data for demonstration only.',
      ended: 'Ended (mock)',
      signOutOthersMock: 'Sign out of other sessions (mock)',
      resetMock: 'Reset (mock)',
    },
    identityProvider: {
      managed:
        'Authentication is managed by your identity provider. The actions below are handled there and are not available in this console.',
      password: 'Password changes',
      mfa: 'Multi-factor authentication',
      login: 'Sign-in',
      refresh: 'Token refresh',
      sessions: 'Session revocation',
    },
    signOut: {
      title: 'Sign out',
      action: 'Sign out',
      description: 'Signing out delegates to your identity provider and ends this session.',
      mockAction: 'Sign out (mock)',
      mockDescription:
        'DEV Mock Auth has no backend session to revoke. This resets your local session only.',
    },
  },
  partnerCredit: {
    title: 'Partner Credit',
    subtitle: 'Configure approved, versioned Partner Credit delivery for each Partner.',
    detailSubtitle: 'Partner {{partnerId}} integration configuration',
    configured: 'Configured',
    notConfigured: 'Not configured',
    emptyPartners: 'No Partners are available for Partner Credit configuration.',
    emptyVersions: 'No immutable configuration versions have been published.',
    partnerList: 'Partner Credit Partner selector',
    pagination: {
      summary: 'Page {{page}} of {{pages}} · {{total}} Partners',
      previous: 'Previous page',
      next: 'Next page',
    },
    versionTable: 'Partner Credit configuration versions',
    versionTitle: 'Partner Credit version',
    versionNotFound: 'This Partner Credit version was not found.',
    secretOmitted: 'Credential secret material is write-only and is never returned or displayed.',
    actions: {
      openPartner: 'Open {{partner}} Partner Credit',
      createVersion: 'Create version',
      preview: 'Preview configuration',
      requestApproval: 'Request approval',
      certify: 'Certify version',
      runCertification: 'Run certification',
      suspend: 'Suspend delivery',
      activate: 'Activate delivery',
    },
    sections: {
      current: 'Current configuration',
      versions: 'Immutable versions',
      publicConfiguration: 'Public configuration',
    },
    fields: {
      id: 'Version ID',
      name: 'Integration name',
      status: 'Delivery status',
      version: 'Version',
      authType: 'Authentication type',
      keyReference: 'Key reference',
      creditEndpoint: 'Credit endpoint',
      statusEndpoint: 'Status endpoint',
      healthEndpoint: 'Health endpoint',
      transactionResultEndpoint: 'Transaction result endpoint',
      transactionResultEndpointHelp:
        'Optional HTTPS callback for terminal transaction delivery results.',
      oauthTokenEndpoint: 'OAuth token endpoint',
      oauthClientId: 'OAuth client ID',
      oauthScopes: 'OAuth scopes',
      oauthAudience: 'OAuth audience',
      timeout: 'Timeout (milliseconds)',
      maximumAttempts: 'Maximum attempts',
      initialDelay: 'Initial delay (seconds)',
      maximumDelay: 'Maximum delay (seconds)',
      multiplier: 'Retry multiplier',
      jitter: 'Jitter (percent)',
      secret: 'Write-only secret',
      reason: 'Reason',
      certifiedAt: 'Certified at',
      publishedAt: 'Published at',
      certification: 'Certification',
      approvalRequestId: 'Approval request ID',
      previewHash: 'Preview hash',
    },
    wizard: {
      title: 'Create Partner Credit version',
      description:
        'Preview the exact public configuration, then request maker-checker approval. No outbound credit is sent.',
      secretNotice:
        'The secret is write-only. It is submitted for preview and approval but is never shown again.',
      previewTitle: 'Normalized preview',
      targetPartner: 'Selected Partner ID',
      currentImpact: 'Current delivery',
      proposedImpact: 'Proposed impact',
      pendingImpact: 'Pending approval and certification; the current delivery remains unchanged.',
      confirmPublish: 'I confirm this exact Partner Credit version and its impact before publish.',
      certificationRequired: 'Certification required before activation.',
      pendingApproval: 'Pending approval: {{id}}',
    },
    certification: {
      title: 'Certify Partner Credit version',
      description: 'Use the pending publish approval request to certify its proposed version.',
      nonFinancial:
        'Certification performs authenticated health and unknown-transfer status requests only. It never sends credit.',
      passed: 'Certification passed · Health HTTP {{health}} · Status HTTP {{status}}',
    },
    status: {
      impact:
        'Delivery changes from {{current}} to {{proposed}}. Activation requires a certified current version.',
      confirm: 'I confirm this delivery status change and its operational impact.',
    },
    health: {
      title: 'Integration health',
      mockNotice:
        'DEV Mock Data for non-financial operational health. No Partner endpoint was contacted.',
      unavailable:
        'Non-financial Partner Credit health is not exposed by the current API. No API request was sent.',
      status: 'Health status',
      successRate: 'Success rate',
      latency: 'Latency',
      retries: 'Retry count',
      unknownResults: 'Unknown results',
      oldestReservation: 'Oldest reservation age',
      exceptions: 'Reconciliation exceptions',
    },
    errors: {
      load: 'Unable to load Partner Credit Partner summaries.',
      loadDetail: 'Unable to load this Partner Credit configuration.',
      loadVersion: 'Unable to load this Partner Credit version.',
      preview: 'Unable to preview this Partner Credit configuration.',
      publish: 'Unable to request approval for this Partner Credit version.',
      certify: 'Unable to certify this Partner Credit version.',
      status: 'Unable to change Partner Credit delivery status.',
    },
  },
  reports: {
    title: 'Reports',
    subtitle: 'Snapshot-bound operational and financial reporting',
    summaryTitle: 'Report-backed summaries',
    unavailableMetric:
      'Metrics not returned by the selected report are shown as unavailable; no values are estimated.',
    activityReport: 'Operational activity report',
    type: 'Report type',
    filters: 'Report filters',
    from: 'From (inclusive)',
    to: 'To (exclusive)',
    timezone: 'Timezone',
    timeBasis: 'Time basis',
    currency: 'Currency',
    status: 'Status',
    partnerId: 'Partner ID',
    agentId: 'Agent ID',
    memberId: 'Member ID',
    uplineMemberId: 'Upline member ID',
    gameTypeId: 'Game type ID',
    gameId: 'Game ID',
    sort: 'Server sort',
    timeBases: {
      PLAY_AT: 'Play time',
      RECEIVED_AT: 'Received time',
      EFFECTIVE_AT: 'Effective time',
      REGISTERED_AT: 'Registration time',
      CREATED_AT: 'Creation time',
      OCCURRED_AT: 'Occurrence time',
    },
    sortOptions: {
      occurredAt: 'Occurred at',
      amount: 'Amount',
      code: 'Code',
      name: 'Name',
    },
    empty: 'No report rows match these filters.',
    stale: 'This is the latest complete snapshot and may be stale.',
    snapshot: 'Snapshot {{id}} · reporting as of {{date}}',
    requestId: 'Request ID: {{id}}',
    columns: {
      occurredAt: 'Occurred at',
      identity: 'Identity',
      status: 'Status',
      currency: 'Currency',
      metrics: 'Metrics',
    },
    pagination: { previous: 'Previous', next: 'Next', server: 'Results use server cursor order.' },
    exportQueued: 'Export queued: {{id}}',
    exportError: 'Unable to queue this report export.',
    kinds: {
      transactions: 'Transactions',
      agentCommissions: 'Agent commissions',
      affiliateCommissions: 'Affiliate commissions',
      games: 'Games',
      registrations: 'Registrations',
      partnerReconciliation: 'Partner reconciliation',
      commissionRuleUsage: 'Commission rule usage',
      auditActivity: 'Audit activity',
      wallets: 'Wallets',
      walletLedger: 'Wallet ledger',
      withdrawals: 'Withdrawals',
      partnerCreditReconciliation: 'Partner Credit reconciliation',
      userAffiliateByGame: 'User affiliate by game',
    },
    meta: {
      title: 'Snapshot metadata',
      snapshotId: 'Snapshot ID',
      timeBasis: 'Time basis',
      timezone: 'Timezone',
      reportingAsOf: 'Reporting as of',
      refreshedAt: 'Refreshed at',
      stale: 'Stale snapshot',
      normalizedFilters: 'Normalized filters',
    },
    totals: {
      title: 'Currency totals',
      playAmount: 'Play amount',
      agentCommissionGross: 'Agent commission gross',
      affiliateCommissionGross: 'Affiliate commission gross',
      commissionReversals: 'Commission reversals',
      commissionAdjustments: 'Commission adjustments',
      netCommission: 'Net commission',
      walletAvailable: 'Wallet available',
      walletReserved: 'Wallet reserved',
      withdrawalRequested: 'Withdrawal requested',
      withdrawalReserved: 'Withdrawal reserved',
      withdrawalCompleted: 'Withdrawal completed',
      withdrawalReleased: 'Withdrawal released',
      withdrawalUnknown: 'Withdrawal unknown',
    },
  },
  reportSummaries: {
    unavailable: 'Unavailable from loaded detail data',
    viewReport: 'View {{report}}',
    metrics: {
      partnerRequests: 'Request total',
      partnerActivity: 'Activity total',
      transactions: 'Transaction total',
      actualPlay: 'Actual play total',
      commission: 'Commission total',
      affiliateByGame: 'Affiliate-by-game summary',
      wallet: 'Wallet summary',
      withdrawals: 'Withdrawal summary',
      generatedCommission: 'Generated commission',
      walletReconciliation: 'Wallet reconciliation',
    },
  },
  reportExports: {
    title: 'Report Exports',
    subtitle: 'Track requested report exports, generation status, and download readiness.',
    empty: 'No report exports match these filters.',
    detail: 'Report export',
    download: 'Download CSV',
    downloadError: 'Unable to download this export.',
    expired: 'This export has expired and can no longer be downloaded.',
    notReady: 'Download becomes available when generation completes.',
    failed: 'This export failed and cannot be downloaded. Review the safe failure code.',
    failureCode: 'Failure code',
    createdAt: 'Requested at',
    requestedBy: 'Requested by',
    partnerScope: 'Partner scope',
    agentScope: 'Agent scope',
    timezone: 'Timezone',
    currency: 'Currency',
    format: 'Format',
    metadataTitle: 'Export metadata',
    filtersTitle: 'Normalized export filters',
    generatedAt: 'Generated at',
    completedAt: 'Completed at',
    updatedAt: 'Updated at',
    expiresAt: 'Expires at',
    snapshotId: 'Snapshot ID',
    recordCount: 'Records',
    contentLength: 'Content length (bytes)',
    filenameNotice: 'The private file is requested only when you choose Download.',
    pagination: { previous: 'Previous', next: 'Next' },
    filters: {
      requestedBy: 'Requester ID',
      partnerId: 'Partner ID',
      agentId: 'Agent ID',
      from: 'Requested from',
      to: 'Requested to',
    },
  },
  capability: {
    backendUnavailable: 'The backend capability is unavailable. No API request was sent.',
  },
  sensitiveAction: {
    reason: 'Reason',
    reasonRequired: 'Reason is required',
    reasonMaximum: 'Reason must be 2,000 characters or fewer',
    evidence: 'Evidence',
    evidenceOptional: 'Evidence (optional)',
    evidenceRequired: 'Evidence is required',
    confirmError: 'Unable to confirm this action. Please try again.',
  },
  actions: {
    apply: 'Apply',
    approve: 'Approve',
    cancel: 'Cancel',
    clear: 'Clear',
    close: 'Close',
    confirm: 'Confirm',
    create: 'Create',
    delete: 'Delete',
    edit: 'Edit',
    export: 'Export',
    reject: 'Reject',
    retry: 'Retry',
    save: 'Save',
    view: 'View',
  },
  filters: {
    dateFrom: 'From date',
    dateTo: 'To date',
    partner: 'Partner',
    agent: 'Agent',
    member: 'Member',
    status: 'Status',
    outcome: 'Outcome',
    code: 'Code',
    username: 'Username',
    partnerUserId: 'Partner user ID',
    entryType: 'Entry type',
    search: 'Search filters',
  },
  dataSource: {
    label: 'Data source',
    mock: 'Mock data',
    real: 'Live API',
    unavailable: 'Unavailable',
  },
  shell: {
    closeNavigation: 'Close navigation',
    collapseNavigation: 'Collapse navigation',
    colorScheme: 'Color scheme',
    darkMode: 'Dark',
    expandNavigation: 'Expand navigation',
    language: 'Language',
    lightMode: 'Light',
    mainContent: 'Main content',
    openNavigation: 'Open navigation',
    primaryNavigation: 'Primary navigation',
  },
  languages: {
    en: 'English',
    th: 'ไทย',
  },
  scaffold: {
    capability: 'Capability',
    knownCapability: 'Known API capability',
    readOnly:
      'This placeholder is read-only. Privileged controls are introduced only with their approved workflow.',
    scopes: {
      authProfile: 'Review the current identity, scope, and effective authorization context.',
      dashboard: 'Monitor affiliate performance, financial signals, and operational queues.',
      partners: 'Review partner organizations, ownership, lifecycle, and commercial scope.',
      agents: 'Review agent accounts, partner assignment, status, and operating scope.',
      adminUsers:
        'Administer backoffice identities, roles, effective permissions, and access status.',
      members: 'Trace member profiles, referral relationships, and affiliate attribution.',
      games: 'Review the game catalog, provider mapping, and affiliate availability.',
      commissionRules:
        'Review commission rule definitions, applicability, versioning, and approval state.',
      transactions:
        'Investigate transaction activity, outcomes, amounts, and affiliate attribution.',
      transactionReversal:
        'Review transaction reversal eligibility, decisions, and resulting state.',
      transactionFailures:
        'Investigate failed transactions, failure reasons, and resolution readiness.',
      apiOperations: 'Review operational API requests, outcomes, and supporting request evidence.',
      commissionLedger:
        'Inspect calculated commission entries, adjustments, and settlement references.',
      commissionAdjustments: 'Review commission adjustment requests, evidence, and ledger impact.',
      audit: 'Trace privileged actions, actors, affected resources, and request evidence.',
      securityEvents:
        'Review authentication, authorization, and suspicious security event signals.',
      wallets: 'Inspect affiliate balances, wallet status, and balance movement history.',
      withdrawals: 'Track affiliate withdrawal requests, processing state, and resolution history.',
      partnerCredit:
        'Configure approved, versioned Partner Credit delivery without exposing secret material.',
      partnerCreditHealth:
        'Monitor non-financial Partner Credit operational health when the backend supports it.',
      approvals: 'Review governed change requests, decision context, and approval history.',
      reconciliation:
        'Compare platform records with provider totals and identify reconciliation exceptions.',
      reportExports: 'Track requested report exports, generation status, and download readiness.',
      reports: 'Review operational and financial reporting across the affiliate platform.',
    },
  },
  accessDenied: 'Access denied',
  api: 'Backoffice API',
  area: 'Backoffice',
  brand: 'Affiliate Platform',
  build: 'Build',
  description:
    'Privileged features remain unavailable until the production identity provider and authorization policy are approved.',
  environment: 'Environment',
  foundation: 'Foundation environment',
  metadata: 'Deployment metadata',
  title: 'Backoffice access is not configured',
  version: 'Version',
} as const;
